import { useRef, useState, useCallback, RefObject, useEffect } from 'react';
import { useRoomSocket } from './useRoomSocket';
import {
  generateCommandId,
  createLogger,
  delay,
} from '@airhead-games/shared/utilities';
import {
  MediaActionType,
  MediaType,
  SenderRoleType,
  GameElementType,
  MediaReadyEvent,
  MediaStartedEvent,
} from '@airhead-games/shared/types';

const logger = createLogger('useMediaSpeechManager');

interface ProcessedSpeechItem {
  mediaId: string;
  speechBuffer: string; // base64-encoded MP3 audio
  roomCode: string;
  elementId?: string;
  elementType?: GameElementType;
  content?: string;
}

// Helper function to decode base64 to Uint8Array
function base64ToUint8Array(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

export function useMediaSpeechManager(
  audioRef: RefObject<HTMLAudioElement | null>,
  isWebAudioAvailable?: boolean
) {
  const speechQueue = useRef<ProcessedSpeechItem[]>([]);
  const currentAudioUrl = useRef<string | null>(null);
  const isProcessingQueue = useRef<boolean>(false);
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  const [speechMediaIdPlaying, setSpeechMediaIdPlaying] = useState<
    string | null
  >(null);
  const nextSpeechPromise = useRef<{ resolve: () => void } | null>(null);
  const nextSpeechMediaIdPromise = useRef<{
    resolve: (mediaId: string) => void;
  } | null>(null);
  const completionPromises = useRef<Map<string, { resolve: () => void }>>(
    new Map()
  );
  const { sendCommand } = useRoomSocket();
  const completedSpeechIds = useRef<Set<string>>(new Set());

  // Helper to check if a mediaId is already in the current queue
  const isMediaIdInQueue = useCallback((mediaId: string): boolean => {
    return speechQueue.current.some((item) => item.mediaId === mediaId);
  }, []);

  const cleanupCurrentAudio = useCallback(() => {
    if (currentAudioUrl.current) {
      URL.revokeObjectURL(currentAudioUrl.current);
      currentAudioUrl.current = null;
    }
  }, []);

  // Helper to send speech-related media commands
  const sendSpeechCommand = useCallback(
    (action: MediaActionType, item: ProcessedSpeechItem) => {
      // Always include speechBuffer (needed for speech recovery)
      sendCommand({
        id: generateCommandId(),
        type: 'MediaElement',
        action,
        mediaType: MediaType.SPEECH,
        senderRole: SenderRoleType.HOST,
        clientTimestamp: Date.now(),
        roomCode: item.roomCode,
        mediaId: item.mediaId,
        content: item.content,
        elementId: item.elementId,
        elementType: item.elementType,
        speechBuffer: item.speechBuffer,
      });
    },
    [sendCommand]
  );

  // Process the next item in the speech queue
  const processQueue = useCallback(async () => {
    if (
      isProcessingQueue.current ||
      speechQueue.current.length === 0 ||
      !audioRef.current
    ) {
      return;
    }

    // Wait for WebAudio service to be available before processing speeches
    if (!isWebAudioAvailable) {
      logger.debug(
        'Speech queue waiting for WebAudio service to be initialized...'
      );
      return;
    }
    isProcessingQueue.current = true;
    setIsSpeaking(true);
    try {
      while (speechQueue.current.length > 0) {
        const nextItem = speechQueue.current[0];
        // Set the currently playing media ID
        setSpeechMediaIdPlaying(nextItem.mediaId);
        // Decode base64 to Uint8Array and create Blob
        const audioBytes = base64ToUint8Array(nextItem.speechBuffer);
        const audioBlob = new Blob([new Uint8Array(audioBytes)], {
          type: 'audio/mpeg',
        });
        cleanupCurrentAudio();
        const audioUrl = URL.createObjectURL(audioBlob);
        currentAudioUrl.current = audioUrl;
        // Set the source
        audioRef.current.src = audioUrl;
        logger.debug(`Loaded speech ${nextItem.mediaId}`);
        // Play and send STARTED/COMPLETED
        try {
          await audioRef.current.play();
          sendSpeechCommand(MediaActionType.STARTED, nextItem);
          logger.debug(`Started speech ${nextItem.mediaId}`);
          await new Promise<void>((resolve) => {
            if (audioRef.current) {
              audioRef.current.onended = () => {
                setSpeechMediaIdPlaying(null);
                resolve();
                if (audioRef.current) {
                  audioRef.current.onended = null;
                }
              };
            } else {
              setSpeechMediaIdPlaying(null);
              resolve();
            }
          });
          sendSpeechCommand(MediaActionType.COMPLETED, nextItem);
          logger.debug(`Completed speech ${nextItem.mediaId}`);
        } catch {
          // Optionally send COMPLETED on error
          sendSpeechCommand(MediaActionType.COMPLETED, nextItem);
          logger.debug(
            `Completed speech (with play error) ${nextItem.mediaId}`
          );
          setSpeechMediaIdPlaying(null);
        }
        // Track completed speech
        completedSpeechIds.current.add(nextItem.mediaId);

        // Resolve completion promise if it exists
        const completionPromise = completionPromises.current.get(
          nextItem.mediaId
        );
        if (completionPromise) {
          completionPromise.resolve();
          completionPromises.current.delete(nextItem.mediaId);
        }
        // Remove from queue and cleanup
        speechQueue.current.shift();
        cleanupCurrentAudio();
        // Add a pause if there is another speech queued
        if (speechQueue.current.length > 0) {
          await delay(1000); // 1s pause between speeches (human-like breath)
        }
      }
    } catch (err) {
      logger.error('Error in processing speech queue:', err);
    } finally {
      cleanupCurrentAudio();
      isProcessingQueue.current = false;
      setIsSpeaking(false);
      setSpeechMediaIdPlaying(null);
    }
  }, [audioRef, cleanupCurrentAudio, sendSpeechCommand, isWebAudioAvailable]);

  // Queue a speech item for playback (called by MediaManager)
  const queueSpeech = useCallback(
    (event: MediaReadyEvent | MediaStartedEvent) => {
      // Skip if currently in the queue or already completed
      if (
        completedSpeechIds.current.has(event.mediaId) ||
        isMediaIdInQueue(event.mediaId)
      ) {
        return;
      }

      // speechBuffer should already be base64 string from backend
      if (!event.speechBuffer || typeof event.speechBuffer !== 'string') {
        logger.error('Invalid speechBuffer received:', {
          mediaId: event.mediaId,
          speechBufferType: typeof event.speechBuffer,
        });
        return;
      }

      logger.debug('Adding speech to queue...', {
        mediaId: event.mediaId,
        bufferSize: event.speechBuffer.length,
        content: event.content,
      });

      speechQueue.current.push({
        mediaId: event.mediaId,
        speechBuffer: event.speechBuffer, // Keep as base64 string
        roomCode: event.roomCode,
        elementId: event.elementId,
        elementType: event.elementType,
        content: event.content,
      });

      // If someone was waiting for the next speech's ID, give it to them.
      if (nextSpeechMediaIdPromise.current) {
        nextSpeechMediaIdPromise.current.resolve(event.mediaId);
        nextSpeechMediaIdPromise.current = null;
      }

      // Auto-start processing if not already running
      if (!isProcessingQueue.current) {
        logger.debug('Starting speech queue processing...');
        processQueue();
      }
    },
    [processQueue, isMediaIdInQueue]
  );

  // Stop speech playback and clear queue
  const stopSpeechPlayback = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    cleanupCurrentAudio();
    speechQueue.current = [];
    isProcessingQueue.current = false;
    completedSpeechIds.current.clear();
    setSpeechMediaIdPlaying(null);
    if (nextSpeechPromise.current) {
      nextSpeechPromise.current.resolve();
      nextSpeechPromise.current = null;
    }
    if (nextSpeechMediaIdPromise.current) {
      nextSpeechMediaIdPromise.current.resolve('');
      nextSpeechMediaIdPromise.current = null;
    }
    completionPromises.current.forEach((p) => p.resolve());
    completionPromises.current.clear();
    setIsSpeaking(false);
  }, [audioRef, cleanupCurrentAudio]);

  // Skip currently playing speech by fast-forwarding to end
  const skipCurrentSpeech = useCallback(() => {
    const audio = audioRef.current;
    if (audio && speechMediaIdPlaying) {
      const { duration } = audio;
      if (!Number.isFinite(duration)) {
        logger.debug(
          `Skip requested for speech ${speechMediaIdPlaying} before duration was available`
        );
        return;
      }

      // Fast-forward to end, triggering natural "ended" event
      // This uses all existing completion logic in processQueue
      audio.currentTime = duration;
      logger.debug(`Skipped to end of speech ${speechMediaIdPlaying}`);
    }
  }, [audioRef, speechMediaIdPlaying]);

  // Skip a speech by its content key (e.g. commentary key) and remove any
  // queued speeches with the same key. If the matching speech is currently
  // playing, it is fast-forwarded to trigger natural completion via onended.
  // Queued (non-playing) matches are silently removed.
  const skipSpeechByContent = useCallback(
    (contentKey: string) => {
      const audio = audioRef.current;
      const isActivelyPlaying = audio && !audio.paused && !audio.ended;

      const currentItem = speechQueue.current[0];

      // If the currently playing speech matches, fast-forward to end.
      // onended will fire → shift() removes it → processQueue continues.
      if (isActivelyPlaying && currentItem?.content === contentKey) {
        const { duration } = audio;
        if (Number.isFinite(duration)) {
          audio.currentTime = duration;
          logger.debug(`Skipped playing speech with content '${contentKey}'`);
        } else {
          logger.debug(
            `Skip requested for speech with content '${contentKey}' before duration was available`
          );
        }
      }

      // Remove any matching speeches from the queue.
      // Skip index 0 if it's actively playing — onended + shift handles that.
      speechQueue.current = speechQueue.current.filter((item, index) => {
        if (index === 0 && isActivelyPlaying) return true;
        return item.content !== contentKey;
      });
    },
    [audioRef]
  );

  // Flush all speech manager state for room transitions
  const flushSpeechManager = useCallback(() => {
    logger.debug('Flushing speech manager state...');
    stopSpeechPlayback();
    // Additional reset for room transitions
    speechQueue.current = [];
    isProcessingQueue.current = false;
    completedSpeechIds.current.clear();
    completionPromises.current.clear();
    setSpeechMediaIdPlaying(null);
    setIsSpeaking(false);
    logger.debug('Speech manager flushed');
  }, [stopSpeechPlayback]);

  const playSpeechBuffer = useCallback(
    async (buffer: Uint8Array) => {
      stopSpeechPlayback();
      const audioBlob = new Blob([new Uint8Array(buffer)], {
        type: 'audio/mpeg',
      });
      const url = URL.createObjectURL(audioBlob);
      if (audioRef.current) {
        audioRef.current.src = url;
        setIsSpeaking(true);
        setSpeechMediaIdPlaying(null);
        audioRef.current.onended = () => {
          setIsSpeaking(false);
          setSpeechMediaIdPlaying(null);
          URL.revokeObjectURL(url);
        };
        try {
          await audioRef.current.play();
          logger.debug('Successfully played speech buffer');
        } catch (err) {
          logger.error('Failed to play speech buffer', err);
        }
      }
      cleanupCurrentAudio();
      if (nextSpeechPromise.current) {
        nextSpeechPromise.current.resolve();
        nextSpeechPromise.current = null;
      }
      if (nextSpeechMediaIdPromise.current) {
        nextSpeechMediaIdPromise.current.resolve('');
        nextSpeechMediaIdPromise.current = null;
      }
    },
    [audioRef, stopSpeechPlayback, cleanupCurrentAudio]
  );

  // Wait for all current/arriving speeches to finish, or resolve after 5s idle timeout if none arrive
  const waitForSpeech = useCallback(() => {
    return new Promise<void>((resolve) => {
      // Wait 5s first
      setTimeout(function pollIdle() {
        if (!isProcessingQueue.current && speechQueue.current.length === 0) {
          resolve();
        } else {
          setTimeout(pollIdle, 100);
        }
      }, 5000);
    });
  }, []);

  // Wait for a specific speech (by mediaId) to finish
  const waitForSpeechById = useCallback((mediaId: string) => {
    return new Promise<void>((resolve) => {
      // Always store the resolver; it will be called when the speech is completed in processQueue
      completionPromises.current.set(mediaId, { resolve });
    });
  }, []);

  // Monitor WebAudio service availability and trigger queue processing when ready
  useEffect(() => {
    if (
      isWebAudioAvailable &&
      speechQueue.current.length > 0 &&
      !isProcessingQueue.current
    ) {
      logger.debug('WebAudio service available, processing queued speeches...');
      processQueue();
    }
  }, [isWebAudioAvailable, processQueue]);

  // Cleanup on unmount
  useEffect(() => {
    const capturedCompletionPromises = completionPromises.current;
    const capturedCompletedSpeechIds = completedSpeechIds.current;
    return () => {
      cleanupCurrentAudio();
      if (nextSpeechPromise.current) {
        nextSpeechPromise.current.resolve();
        nextSpeechPromise.current = null;
      }
      if (nextSpeechMediaIdPromise.current) {
        nextSpeechMediaIdPromise.current.resolve('');
        nextSpeechMediaIdPromise.current = null;
      }
      capturedCompletionPromises.forEach((p) => p.resolve());
      capturedCompletionPromises.clear();
      capturedCompletedSpeechIds.clear();
    };
  }, [cleanupCurrentAudio]);

  return {
    isSpeaking,
    speechMediaIdPlaying,
    waitForSpeech,
    waitForSpeechById,
    queueSpeech,
    stopSpeechPlayback,
    skipCurrentSpeech,
    skipSpeechByContent,
    playSpeechBuffer,
    flushSpeechManager,
  };
}
